import {
    Dimensions,
    PixelRatio,
    StyleSheet,
    ViewStyle,
    TextStyle,
    ImageStyle,
} from "react-native";

// ---- CONFIG ---- //

const BASE_WIDTH = 412;      // Pixel 9
const BASE_HEIGHT = 915;
const DEBUG = false;         // toggle logs

// Optional: auto scale width/height values
const SCALE_LAYOUT_DIMENSIONS = false;

// ---- SCREEN ---- //

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } =
    Dimensions.get("screen");

// ---- CLAMP UTIL ---- //

const clamp = (min: number, value: number, max: number) =>
    Math.min(Math.max(value, min), max);

// ---- CORE SCALE ---- //

export const scale = (size: number): number =>
    clamp(size * 0.85, (SCREEN_WIDTH / BASE_WIDTH) * size, size * 1.15);

export const verticalScale = (size: number): number =>
    clamp(size * 0.85, (SCREEN_HEIGHT / BASE_HEIGHT) * size, size * 1.15);

// Moderate scale: best for fonts
export const moderateScale = (size: number, factor = 0.5): number =>
    size + (scale(size) - size) * factor;

// Accessibility-aware font scaling
export const fontSize = (size: number): number =>
    moderateScale(size / PixelRatio.getFontScale(), 0.6);

// ---- VW / VH Helpers ---- //

export const vw = (w: number): number => {
    const percent = (w / BASE_WIDTH) * 100;
    return PixelRatio.roundToNearestPixel((SCREEN_WIDTH * percent) / 100);
};

export const vh = (h: number): number => {
    const percent = (h / BASE_HEIGHT) * 100;
    return PixelRatio.roundToNearestPixel((SCREEN_HEIGHT * percent) / 100);
};

// ---- TYPES ---- //

type NamedStyles<T> = { [P in keyof T]: ViewStyle | TextStyle | ImageStyle };
type StyleValue = number | string | object | undefined | null;
type StyleObject = Record<string, StyleValue>;

// ---- PROPERTY RULES ---- //

const FONT_KEYS = new Set(["fontSize", "lineHeight", "letterSpacing"]);

const WIDTH_KEYS = new Set([
    "margin",
    "marginLeft",
    "marginRight",
    "marginHorizontal",
    "padding",
    "paddingLeft",
    "paddingRight",
    "paddingHorizontal",
    "left",
    "right",
]);

const HEIGHT_KEYS = new Set([
    "marginTop",
    "marginBottom",
    "marginVertical",
    "paddingTop",
    "paddingBottom",
    "paddingVertical",
    "top",
    "bottom",
]);

const BORDER_KEYS = new Set(["borderRadius", "borderWidth"]);

// ---- SCALE UTILITY ---- //

const scaleObj = (obj: StyleObject): StyleObject => {
    const out: StyleObject = {};

    for (const key in obj) {
        const value = obj[key];

        if (typeof value === "number") {
            let newVal: number = value;

            if (FONT_KEYS.has(key)) {
                // newVal = fontSize(value);
                newVal = value;
            } else if (WIDTH_KEYS.has(key)) {
                newVal = scale(value);
            } else if (HEIGHT_KEYS.has(key)) {
                newVal = verticalScale(value);
            } else if (BORDER_KEYS.has(key)) {
                newVal = moderateScale(value);
            } else if (SCALE_LAYOUT_DIMENSIONS && key.toLowerCase().includes("width")) {
                newVal = scale(value);
            } else if (SCALE_LAYOUT_DIMENSIONS && key.toLowerCase().includes("height")) {
                newVal = verticalScale(value);
            }

            DEBUG && console.log(`[ScaledSheet] ${key}: ${value} → ${newVal}`);
            out[key] = newVal;
        }

        else if (
            typeof value === "object" &&
            value !== null &&
            !Array.isArray(value)
        ) {
            out[key] = scaleObj(value as StyleObject);
        }

        else {
            out[key] = value;
        }
    }

    return out;
};

// Scale a single style object
export const scaleStyle = <T extends ViewStyle | TextStyle | ImageStyle>(
    style: T
): T => {
    return scaleObj(style as StyleObject) as unknown as T;
};

// ---- SCALED SHEET ---- //

export const ScaledSheet = {
    create: <T extends NamedStyles<T> | NamedStyles<any>>(
        rawStyles: T | NamedStyles<T>
    ): T => {
        const scaledStyles = {} as T;

        for (const key in rawStyles) {
            scaledStyles[key] = scaleObj(
                rawStyles[key] as StyleObject
            ) as unknown as T[Extract<keyof T, string>];
        }

        return StyleSheet.create(scaledStyles);
    },
};
